<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted">Loading data...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!items || items.length === 0" class="text-center py-5">
      <i
        class="fe fe-inbox text-muted"
        style="font-size: 48px; opacity: 0.6"
      ></i>
      <p class="mt-3 text-muted">Tidak ada data untuk ditampilkan.</p>
    </div>

    <!-- Card List (Grid) -->
    <div v-else class="row">
      <div
        class="col-lg-3 col-md-6 col-sm-12 mb-4"
        v-for="course in items"
        :key="course.id"
      >
        <!-- Card Component -->
        <div class="card h-100 shadow-sm border" style="cursor: pointer">
          <!-- Image -->
          <div
            class="position-relative"
            @click.stop="$emit('open-detail', course, type)"
          >
            <img
              :src="
                course?.cover_image_path ||
                `https://dummyimage.com/300x200/FAFAFA/000000&text=${course.title || 'Course'}`
              "
              :alt="course.title"
              class="card-img-top"
              style="height: 180px; object-fit: cover"
            />
            <div
              v-if="course?.training?.provider?.provider_logo_path"
              class="position-absolute"
              style="top: 8px; right: 8px"
            >
              <div
                class="bg-white rounded shadow-sm p-1"
                style="
                  display: flex;
                  align-items: center;
                  justify-content: center;
                "
              >
                <img
                  :src="
                    course?.training?.provider?.provider_logo_path ||
                    `https://dummyimage.com/16x16/ffffff/000000&text=${course?.training?.provider?.name || 'Provider+title'}`
                  "
                  :alt="course.provider_name"
                  style="height: 16px; object-fit: cover"
                />
              </div>
            </div>

            <!-- Training Status -->
            <div
              v-if="course.status"
              class="position-absolute"
              style="top: 8px; left: 8px"
            >
              <span :class="getTrainingStatusClass(course.status)">
                {{ getApprovalStatusLabel(course.status) }}
              </span>
            </div>

            <!-- TNA Status & GAP Competencies -->
            <div
              v-if="course.difference != null"
              class="position-absolute d-flex gap-2"
              style="bottom: 8px; left: 8px"
            >
              <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                <b-icon
                  icon="file-earmark-bar-graph"
                  style="font-size: 0.75rem"
                ></b-icon>
                Gap
                <span
                  :class="
                    course.difference < 1
                      ? 'badge bg-success'
                      : 'badge bg-danger'
                  "
                >
                  {{ course.difference }}
                </span>
              </div>
              <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                <b-icon
                  icon="star-fill"
                  class="text-success"
                  style="font-size: 0.75rem"
                ></b-icon>
                <span>
                  {{ course.proficiency_standard }}
                </span>
              </div>
              <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                <b-icon
                  icon="star-fill"
                  class="text-warning"
                  style="font-size: 0.75rem"
                ></b-icon>
                <span>
                  {{ course.proficiency }}
                </span>
              </div>
            </div>
          </div>

          <!-- Card Body -->
          <div
            class="card-body p-3"
            @click.stop="$emit('open-detail', course, type)"
          >
            <!-- Badges Row -->
            <div
              v-if="course.competencies && course.competencies.length"
              class="mb-2 d-flex flex-wrap gap-1"
            >
              <span class="badge-icon bg-danger-soft text-danger">
                <i
                  :class="getTrainingTypeIcon(course.training_type)"
                  style="font-size: 10px"
                ></i>
              </span>

              <span
                v-if="course.competencies && course.competencies.length"
                class="badge-custom bg-warning-soft text-warning"
              >
                {{ course.competencies[0].role_competency || "-" }}
              </span>

              <span
                v-if="course.competencies && course.competencies.length"
                class="badge-custom bg-info-soft text-info"
              >
                {{ course.competencies[0].type || "-" }}
              </span>
            </div>

            <!-- Title -->
            <h6
              class="card-title font-weight-bold"
              style="font-size: 0.95rem; min-height: 10px"
            >
              {{ course.title }}
            </h6>

            <!-- Description -->
            <p
              v-if="course.description"
              class="card-text text-muted course-description"
            >
              {{ truncateText(course.description, 80) }}
            </p>
          </div>

          <!-- Footer -->
          <div class="card-footer bg-white border-top px-3 py-2">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex gap-3">
                <!-- Duration -->
                <div
                  v-if="course?.hour != null"
                  class="d-flex align-items-center"
                >
                  <i
                    class="fe fe-clock text-muted me-1"
                    style="font-size: 0.85rem"
                  ></i>
                  <small class="text-muted" style="font-size: 0.75rem">
                    {{ Number(course.hour || 0) }}h
                  </small>
                </div>
                <!-- Currency -->
                <div
                  v-if="course?.currency != null || course?.price != null"
                  class="d-flex align-items-center"
                >
                  <small class="text-muted" style="font-size: 0.75rem">
                    {{ course?.currency || "" }}
                    {{ formatCurrency(course?.currency, course?.price) }}
                  </small>
                </div>
              </div>

              <div v-if="isFooter" class="d-flex align-items-center">
                <!-- Detail Button -->
                <button
                  class="btn btn-sm btn-outline-danger ml-3"
                  @click.stop="$emit('open-detail', course, type)"
                >
                  <i class="fe fe-eye" style="font-size: 0.8rem"></i>
                </button>
                <!-- Selection Checkbox -->
                <input
                  v-if="isCanSelect"
                  type="checkbox"
                  class="form-check-input checkbox-lg cursor-pointer"
                  :checked="
                    selectedItems.some((i) => i.id === course.id) ||
                    course.status !== 'open'
                  "
                  :disabled="forceSelect || course.status !== 'open'"
                  @change="onSelectChange(course, $event)"
                  aria-label="Select course"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- End Card Component -->
      </div>
    </div>
  </div>
</template>

<script>
import { titleCase } from "@/utils/titleCase";
import { getApprovalStatusLabel } from "@/utils/approvalStatus";
import { getTrainingStatusClass } from "../utils/getTrainingStatusClass";
import { getTrainingTypeIcon } from "../utils/getTrainingTypeIcon";
import { truncateText } from "@/utils/truncateText";
import { formatCurrency } from "@/utils/formatCurrency";

export default {
  name: "CardGrid",

  props: {
    isLoading: Boolean,
    handleOpenModalDetail: Function,
    isFooter: {
      type: Boolean,
      default: true,
    },
    isCanSelect: {
      type: Boolean,
      default: false,
    },
    forceSelect: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "social",
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    imageBaseUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {};
  },
  methods: {
    titleCase,
    getApprovalStatusLabel,
    truncateText,
    getTrainingStatusClass,
    getTrainingTypeIcon,
    formatCurrency,
    onSelectChange(course, e) {
      if (this.forceSelect) return;
      this.$emit("select-change", course, e.target.checked);
    },
  },
};
</script>

<style>
.badge-custom {
  padding: 0.25rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 0.25rem;
}
</style>
